import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  FormField,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {head, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceOrderJobQuery,
  usePutServiceOrderItemsAssignMechanicMutation,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  bulkAssignMechanicFormSchema,
  BulkAssignMechanicFormType,
} from '../types/bulkAssignMechanicFormSchema';

interface BulkAssignMechanicFormProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  items: {serviceJobId: string; serviceItemId: string}[];
  onSubmit: VoidFunction;
  onClose: VoidFunction;
}

export function BulkAssignMechanicForm(props: BulkAssignMechanicFormProps) {
  const {data, isLoading, isError} = useGetServiceOrderJobQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
    serviceJobId: head(props.items)?.serviceJobId ?? '',
  });

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNil(props.serviceCaseId)}
  );

  const {data: mechanics, isLoading: isMechanicOptionsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: serviceCase?.authorizationProfileId ?? '',
      isActive: true,
      withToOffer: true,
    },
    {
      skip:
        isNil(serviceCase?.authorizationProfileId) ||
        !isFeatureEnabled(featureFlags.ACL_EMPLOYEE_MANAGEMENT),
    }
  );

  const mechanicOptions =
    mechanics?.map((mechanic) => ({
      label: mechanic?.name,
      value: mechanic?.employeeId,
    })) ?? [];

  const [putServiceOrderItemsAssignMechanic] = usePutServiceOrderItemsAssignMechanicMutation();

  const handleSubmit: FormSubmitHandler<BulkAssignMechanicFormType> = async (data) => {
    await putServiceOrderItemsAssignMechanic({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        serviceItems: props.items.map((item) => ({
          serviceItemId: item.serviceItemId,
          serviceJobId: item.serviceJobId,
        })),
        assignMechanicId: data.assignMechanicId ?? '',
      },
    })
      .unwrap()
      .then(() => {
        props.onSubmit();
        props.onClose();
      })
      .catch(handleApiError);
  };

  const defaultValues: Partial<BulkAssignMechanicFormType> = {
    assignMechanicId: head(data?.assignMechanics?.filter(isNotNil) ?? [])?.id ?? undefined,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={34}>
      <Form<BulkAssignMechanicFormType>
        experimentalZodSchema={bulkAssignMechanicFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              isRequired
              isNotClearable
              control={control}
              label={i18n.t('entity.order.labels.mechanic')}
              name="assignMechanicId"
              type="choice"
              options={mechanicOptions}
              isLoading={isMechanicOptionsLoading}
              data-testid={suffixTestId('mechanic', props)}
              menuInPortal
            />
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('discardButton', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.save')}
                data-testid={suffixTestId('saveButton', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
